import React, { useState } from "react";
import {
  TextField,
  Grid,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { getUserPermissions } from "src/utils/permissionsUtils";
import HorseRacingTableBody from "./HorseRacingTableBody";
import HorseNamesConfig from "./HorseNamesConfig";

export default function HorseRacingConfig({
  gameConfig,
  setGameConfig,
  loading = false,
  gameProvider,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const { isViewer } = getUserPermissions();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  const initializeData = () => {
    if (!gameConfig.configs || !Array.isArray(gameConfig.configs)) {
      return Array(2)
        .fill(null)
        .map(() =>
          Array(6)
            .fill(null)
            .map(() => ({
              multiplier: 1.0,
              winProbability: 0.0,
            }))
        );
    }
    return gameConfig.configs;
  };

  const horseRacingData = initializeData();

  const handleInputChange = (rowIndex, colIndex, field, value) => {
    const newData = [...horseRacingData];
    newData[rowIndex][colIndex] = {
      ...newData[rowIndex][colIndex],
      [field]: parseFloat(value) || 0,
    };

    setGameConfig({
      ...gameConfig,
      configs: newData,
    });
  };

  const handleAddRow = () => {
    const newRow = Array(6)
      .fill(null)
      .map(() => ({
        multiplier: 1.0,
        winProbability: 0.0,
      }));

    const newData = [...horseRacingData, newRow];
    setGameConfig({
      ...gameConfig,
      configs: newData,
    });
  };

  const handleDeleteRow = (rowIndex) => {
    if (horseRacingData.length <= 1) return;

    const newData = horseRacingData.filter((_, index) => index !== rowIndex);
    setGameConfig({
      ...gameConfig,
      configs: newData,
    });
  };

  const handleEntryChange = (entryIndex, value) => {
    const newEntries = [...(gameConfig.entries || Array(8).fill(""))];
    newEntries[entryIndex] = value;
    setGameConfig({
      ...gameConfig,
      entries: newEntries,
    });
  };

  console.log("Horse Racing Data:", gameConfig);

  const handleExportCSV = () => {
    const headers = ["Race"];
    for (let i = 0; i < 6; i++) {
      headers.push(
        `Horse ${i + 1} Multiplier`,
        `Horse ${i + 1} Win Probability`,
        `Horse ${i + 1} RTP`
      );
    }
    headers.push("Total Probability", "Avg RTP");

    const rows = horseRacingData.map((raceRow, raceIndex) => {
      const row = [`Race ${raceIndex + 1}`];
      raceRow.forEach((horse) => {
        const multiplier = horse.multiplier;
        const winProbability = horse.winProbability;
        const rtp = multiplier * winProbability;
        row.push(multiplier, winProbability, rtp.toFixed(4));
      });

      const totalProbability = raceRow.reduce(
        (sum, horse) => sum + (parseFloat(horse.winProbability) || 0),
        0
      );
      const totalRtp = raceRow.reduce(
        (sum, horse) =>
          sum +
          (parseFloat(horse.multiplier) || 0) *
            (parseFloat(horse.winProbability) || 0),
        0
      );
      const avgRtp = totalRtp / raceRow.length || 0;

      row.push(
        `'${totalProbability.toFixed(4)}`,
        `'${(avgRtp * 100).toFixed(2)}%`
      );

      return row;
    });

    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "horse_racing_config.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Horse Racing Configuration
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="horse racing tabs"
            sx={{ flexGrow: 1 }}
          >
            <Tab label="Races" />
            <Tab label="Entries" />
            <Tab label="Horse Names" />
          </Tabs>
          {activeTab === 0 && (
            <Button
              variant="outlined"
              onClick={handleExportCSV}
              size="small"
              sx={{
                borderColor: "#1976d2",
                color: "#1976d2",
                ml: 2,
                minWidth: 120,
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "#e3f2fd",
                  borderColor: "#1565c0",
                },
              }}
            >
              Export CSV
            </Button>
          )}
        </Box>

        {activeTab === 0 && (
          <Box sx={{ mt: 2 }}>
            <TableContainer
              component={Paper}
              sx={{ boxShadow: 3, borderRadius: 2 }}
            >
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                        borderRight: "1px solid #e0e0e0",
                        backgroundColor: "#e3f2fd",
                        minWidth: 100,
                      }}
                    >
                      Race
                    </TableCell>
                    {Array(6)
                      .fill(null)
                      .map((_, index) => (
                        <TableCell
                          key={`header-${index}`}
                          align="center"
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            borderRight: "1px solid #e0e0e0",
                            backgroundColor: "#e8f5e8",
                            minWidth: 140,
                          }}
                        >
                          Horse {index + 1}
                        </TableCell>
                      ))}
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                        backgroundColor: "#fff3e0",
                        minWidth: 100,
                      }}
                    >
                      Total Probability
                    </TableCell>
                  </TableRow>
                </TableHead>
                <HorseRacingTableBody
                  horseRacingData={horseRacingData}
                  handleInputChange={handleInputChange}
                  handleDeleteRow={handleDeleteRow}
                  loading={loading}
                  isViewer={isViewer}
                />
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddRow}
                disabled={loading}
                sx={{
                  backgroundColor: "#2e7d32",
                  "&:hover": {
                    backgroundColor: "#1b5e20",
                  },
                }}
              >
                Add New Race
              </Button>
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ mt: 2, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Entry Configuration
            </Typography>
            <Grid container spacing={3}>
              {Array(8)
                .fill(null)
                .map((_, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={`entry-${index}`}
                  >
                    <TextField
                      fullWidth
                      label={`Entry ${index + 1}`}
                      value={gameConfig.entries?.[index] || ""}
                      onChange={(e) => handleEntryChange(index, e.target.value)}
                      variant="outlined"
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#ffffff",
                          "&:hover": {
                            backgroundColor: "#f8f9fa",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#ffffff",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "#1976d2",
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}

        {activeTab === 2 && (
          <HorseNamesConfig
            gameConfig={gameConfig}
            setGameConfig={setGameConfig}
            loading={loading}
          />
        )}
      </Grid>
    </Grid>
  );
}
